// EST-FEAT-04 — Quick Quote: a live ballpark computed from the property's Google Solar roof
// measurement, always visible with no assessment or engagement action needed, plus (for a bound
// engagement) the ability to adjust the inputs and save an official versioned quote on record.
// Bound to workflow statuses through the workflow definition's `module_displays`
// (`quick_quote_panel`), NOT a status check here.
//
// This used to be two cards stacked on the workflow card: this section (automatic, no save) and
// a separate EngagementQuickQuoteCard (empty until a Sales Agent clicked "Generate & save"). Both
// were labeled "Quick Quote" and read as duplicates, so they're merged — the live number from the
// roof measurement IS the default number; there is nothing to generate before you have something
// to look at. Editing/saving now acts on that same number instead of a second, parallel one.
//
// Adjust -> Regenerate -> Save is the FR-04-003 loop: `Regenerate` calls the preview endpoint and
// persists nothing, so an abandoned exploration leaves no ballpark on the engagement that someone
// later mistakes for a quoted price; only `Save` writes a version (EST-FR-04-005).
//
// Read-only w.r.t. the property record otherwise: traced shapes on the satellite map are
// local-only scratch space (no `onEdit`/SAM-context push — this isn't a chat surface).
import { tracedRoofAreaSqft } from '@/components/QuickQuote/tracedArea';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/UI/Button';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useIsTenantAdmin } from '@/hooks/useIsTenantAdmin';
import { useSamSurfaceContext } from '@/hooks';
import { useLazyGetPropertyRoofEstimateQuery } from '@/redux/internal-services/workspaceApiExtensions';
import {
  useGetCurrentQuickQuoteQuery,
  useSaveQuickQuoteMutation,
  type QuickQuote,
  type QuickQuoteAdjustments,
} from '@/redux/internal-services/quickQuotesApi';
import { useUpdateCatalogueConfigMutation } from '@/redux/internal-services/platformAdminApi';
import { QuickQuoteMap, type DrawnShape, type QuickQuoteMapHandle } from '@/components/QuickQuote/QuickQuoteMap';
import RoofPitchPanel from '@/components/QuickQuote/RoofPitchPanel';
import TracedMeasurements from '@/components/QuickQuote/TracedMeasurements';
import QuickQuoteCostEstimate from '@/components/QuickQuote/QuickQuoteCostEstimate';
import { formatPitch, pitchMultiplier } from '@/lib/roofPitch';

const UNIT_OPTIONS = [
  { value: 'sqft', label: 'sqft' },
  { value: 'square', label: 'square (100 sqft)' },
  { value: 'sqm', label: 'sqm' },
];

const fmt = (n: number | null | undefined, digits = 0): string =>
  n == null ? '' : n.toLocaleString(undefined, { maximumFractionDigits: digits });

const fmtDateTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

/** The backend surfaces its refusals as a `detail` string (never a fabricated $0 quote), so the
 *  reason — no measured area vs. no configured rate, which have different fixes — has to reach
 *  the user rather than collapsing into a generic failure. */
const errorDetail = (err: unknown, fallback: string): string => {
  const data = (err as { data?: { detail?: string; message?: string } } | undefined)?.data;
  return data?.detail || data?.message || fallback;
};

/** Inputs and assumptions behind a saved/previewed quote — EST-FR-04-002 requires these to be
 *  presented WITH the result, and the provenance fields are the point: an eyeballed area and a
 *  measured one must never read the same, nor a per-job complexity override and the tenant's
 *  configured rate. Only rendered once a QuickQuote/preview exists — the live property estimate
 *  below has no per-job provenance to report yet. */
function QuoteBasis({ quote }: { quote: QuickQuote }) {
  const { t } = useTranslation();
  const manualArea = quote.inputs.area_source === 'MANUAL';
  const overriddenComplexity = quote.pricing_basis.complexity_source === 'AGENT_OVERRIDE';

  return (
    <div className="space-y-0.5 text-xs text-ui-text3">
      <p>
        {t('quickQuote.basisArea', 'Area')}: <span className="font-medium text-ui-text2">{fmt(quote.inputs.area_sqft)} sqft</span>
        {' · '}
        {manualArea
          ? t('quickQuote.areaSourceManual', 'entered manually')
          : t('quickQuote.areaSourceAi', 'AI measurement')}
        {!manualArea && quote.inputs.imagery_date && (
          <> {t('quickQuote.imageryFrom', '(imagery {{date}})', { date: quote.inputs.imagery_date })}</>
        )}
      </p>
      <p>
        {t('quickQuote.basisRate', 'Rate')}: <span className="font-medium text-ui-text2">
          ${fmt(quote.pricing_basis.price_per_unit, 2)}/{quote.pricing_basis.unit}
        </span>
        {' · '}
        {t('quickQuote.basisComplexity', 'complexity')} ×{fmt(quote.pricing_basis.complexity_factor, 2)}
        {overriddenComplexity && (
          <span className="ml-1 rounded bg-warning/15 px-1 py-px font-medium text-warning-text">
            {t('quickQuote.complexityOverridden', 'overridden')}
          </span>
        )}
      </p>
    </div>
  );
}

interface QuickQuoteContextState {
  expanded: boolean;
  map_open: boolean;
  has_estimate: boolean;
  has_saved_quote: boolean;
  estimate_range?: string;
  /** The live/traced numbers SAM needs to answer "what's the estimate showing" without a tool
   *  call — mirrors `_norm_quick_quote`'s expected fields in
   *  api-service/app/services/llm/ui_context.py, which renders these into both the CURRENT UI
   *  CONTEXT prompt block and the live [SCREEN UPDATE] note. */
  address?: string;
  ai_roof_area_sqft?: number;
  ai_squares?: number;
  traced_squares_total?: number;
  shape_count?: number;
  applied_pitch?: string;
}

interface PropertyRoofEstimateSectionProps {
  propertyId: string;
  engagementId: string;
  /** Collapses the card back to WorkflowCard's summary pill. The card's own header doubles as
   *  the toggle (see the header button below) instead of a separate "Hide Quick Quote" bar sitting
   *  on top of it — two stacked header-shaped bars read as duplicated chrome. */
  /** Set when the user reached this panel by pressing "Quick Quote" on the collapsed pill and no
   *  quote is stored yet — that press IS the request to measure and quote, so the flow runs on
   *  arrival instead of making them press a second button to get the same thing. */
  autoGenerate?: boolean;
  onHide: () => void;
  /** When true, auto-open the satellite map (SAM requested the map for this engagement). Only
   *  the rising edge triggers the open — subsequent renders with the same value are ignored. */
  openSatelliteMap?: boolean;
  /** The engagement is closed: show the stored quote but accept no edits or saves (QA #16). */
  readOnly?: boolean;
}

export function PropertyRoofEstimateSection({
  propertyId, engagementId, autoGenerate = false, onHide, openSatelliteMap, readOnly = false,
}: PropertyRoofEstimateSectionProps) {
  const { t } = useTranslation();
  // The roof estimate is a Google Solar lookup. It used to run the moment this panel opened, so
  // simply LOOKING at Quick Quote measured the property and put a firm price on screen before
  // anyone asked for one. Now it is lazy: nothing is fetched until the user presses the button,
  // or until we already have a saved quote to show alongside.
  const [fetchRoofEstimate, { data, isLoading, isError }] = useLazyGetPropertyRoofEstimateQuery();
  const isTenantAdmin = useIsTenantAdmin();
  const [updateCatalogueConfig] = useUpdateCatalogueConfigMutation();
  const mapRef = useRef<QuickQuoteMapHandle | null>(null);
  const [shapes, setShapes] = useState<DrawnShape[]>([]);
  // Gates the billed Google Maps instantiation until the user actually asks for it (see below).
  const [showMap, setShowMap] = useState(false);
  // Auto-open the map when SAM requests it (openSatelliteMap rising edge).
  // Also fetch the roof estimate if not yet loaded — without data.lat/lng the map branch
  // never renders, so setShowMap alone is not enough when visiting for the first time.
  useEffect(() => {
    if (!openSatelliteMap) return;
    setShowMap(true);
    void fetchRoofEstimate(propertyId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSatelliteMap]);
  // Traced footprints are corrected with the measured pitch automatically (see RoofPitchPanel).
  const activePitch = data?.predominant_pitch ? parseFloat(data.predominant_pitch) : null;

  // ── saved/versioned engagement quote (EST-FEAT-04) ──────────────────────────────────────────
  const permissions = useAppSelector((s) => s.sessionState.permissions);
  const tenantId = useAppSelector((s) => s.sessionState.tenantId);
  // `all_data` is the platform-level bypass, honoured as AuthGuard and the backend's
  // RequirePermissions do it. Authoring additionally needs a tenant context: a platform session
  // has none, and the backend refuses such a write rather than orphan a row no tenant user could
  // read back. The live estimate below still shows either way — it is only Save/Adjust that need this.
  const canCreate = !readOnly && ((permissions.includes('all_data') && !!tenantId) || permissions.includes('quick_quote:create'));

  const { data: current, isLoading: currentLoading } = useGetCurrentQuickQuoteQuery(engagementId);
  // A saved quote already means someone triggered this, so loading its measurement back is not a
  // new trigger — it is showing what the stored quote was built from.
  // Revisiting a property must NOT re-measure it: a stored quote already carries its own area,
  // rate and totals, so the panel renders from `current` alone. (This used to re-fetch the roof
  // estimate whenever a saved quote existed, which called out to the roof/Solar lookup on every
  // visit for data the quote already had.)
  //
  // The one time a fetch happens unasked is the FIRST press of "Quick Quote" on a property with
  // nothing stored — that press is the request. Guarded by a ref so a re-render, or the query
  // settling, can never fire it twice.
  const autoGenerateFired = useRef(false);
  useEffect(() => {
    if (!autoGenerate || autoGenerateFired.current) return;
    if (current || currentLoading) return;   // already quoted, or still finding out
    autoGenerateFired.current = true;
    // A viewer who cannot author must not have a write fired on their behalf. The pill that sets
    // `autoGenerate` (WorkflowCard) is open to anyone who can see the engagement, so without this
    // check simply pressing "Quick Quote" POSTed a quote the API refuses — and the card opened on
    // a red save error instead of the estimate they came to look at. They still get the
    // measurement, which only needs property:read; it is only STORING it that needs the
    // permission, exactly as the canCreate comment above says.
    if (canCreate) void onSave();
    else void fetchRoofEstimate(propertyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate, current, currentLoading, canCreate]);

  // Report Quick Quote's visible state to SAM as active_quick_quote whenever it changes —
  // useSamSurfaceContext reports null on unmount automatically, so closing this card never needs
  // its own cleanup effect.
  const quickQuoteSurfaceState = useMemo<QuickQuoteContextState>(() => {
    const low = current?.result?.estimated_cost_low ?? data?.estimated_cost_low ?? null;
    const high = current?.result?.estimated_cost_high ?? data?.estimated_cost_high ?? null;
    const estimateRange = low != null
      ? `$${Math.round(low / 1000)}k${high != null && high !== low ? `–$${Math.round(high / 1000)}k` : ''}`
      : undefined;
    // Same total TracedMeasurements renders (footprint × pitch factor), duplicated here since that
    // component only displays the number, it doesn't report it back up to this push.
    const polygons = shapes.filter((s) => s.type === 'polygon');
    const footprintTotal = polygons.reduce((sum, s) => sum + (s.area_sqft ?? 0), 0);
    const tracedSquaresTotal = polygons.length > 0
      ? (footprintTotal * (activePitch != null ? pitchMultiplier(activePitch) : 1)) / 100
      : undefined;
    return {
      expanded: true,
      map_open: showMap,
      has_estimate: !!(data || current),
      has_saved_quote: !!current,
      estimate_range: estimateRange,
      address: data?.address,
      ai_roof_area_sqft: data?.ai_roof_area_sqft ?? undefined,
      ai_squares: data?.ai_squares ?? undefined,
      traced_squares_total: tracedSquaresTotal,
      shape_count: shapes.length || undefined,
      applied_pitch: activePitch != null ? formatPitch(activePitch) : undefined,
    };
  }, [showMap, data, current, shapes, activePitch]);
  useSamSurfaceContext('quick_quote', quickQuoteSurfaceState);

  const [adjusting, setAdjusting] = useState(false);
  const [areaInput, setAreaInput] = useState('');
  const [complexityInput, setComplexityInput] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [unitInput, setUnitInput] = useState('square');
  // Whether the user actually edited the field, as opposed to leaving it at whatever it was
  // seeded with. The area/complexity fields now pre-fill from the live estimate (see
  // startAdjusting below) instead of starting blank, so "non-blank" alone no longer means "the
  // user supplied this" — regenerating without touching a pre-filled field must still omit it,
  // exactly like leaving the old blank field alone did, or an untouched AI-sourced number gets
  // saved as area_source: MANUAL.
  const [areaTouched, setAreaTouched] = useState(false);
  const [complexityTouched, setComplexityTouched] = useState(false);
  const [priceTouched, setPriceTouched] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  const [runSave, { isLoading: saving }] = useSaveQuickQuoteMutation();

  const adjustments = (): QuickQuoteAdjustments => {
    const adj: QuickQuoteAdjustments = {};
    if (areaTouched) {
      const area = parseFloat(areaInput);
      if (areaInput.trim() !== '' && Number.isFinite(area) && area > 0) adj.area_sqft = area;
    }
    if (complexityTouched) {
      const complexity = parseFloat(complexityInput);
      if (complexityInput.trim() !== '' && Number.isFinite(complexity)) adj.complexity_factor = complexity;
    }
    if (priceTouched) {
      const price = parseFloat(priceInput);
      if (priceInput.trim() !== '' && Number.isFinite(price) && price > 0) adj.price_per_unit = price;
    }
    return adj;
  };

  // A touched field must hold a usable number. Blank means "use the default" and is left alone;
  // a 0 or out-of-range value used to be silently dropped (area/price) or sent and refused by the
  // API (complexity), so say what to fix before anything is saved.
  const invalidFields = (): string[] => {
    const bad: string[] = [];
    const isBad = (touched: boolean, raw: string, ok: (n: number) => boolean) => {
      if (!touched || raw.trim() === '') return false;
      const n = parseFloat(raw);
      return !Number.isFinite(n) || !ok(n);
    };
    if (isBad(areaTouched, areaInput, (n) => n > 0)) bad.push('roof area');
    if (isBad(priceTouched, priceInput, (n) => n > 0)) bad.push('price per unit');
    if (isBad(complexityTouched, complexityInput, (n) => n >= 1 && n <= 3)) bad.push('complexity factor (1–3)');
    return bad;
  };

  const onSave = async (areaOverride?: number) => {
    setQuoteError(null);
    const invalid = invalidFields();
    if (invalid.length > 0) {
      setQuoteError(t('quickQuote.invalidValues', 'Enter a valid {{fields}}.', { fields: invalid.join(', ') }));
      return;
    }
    try {
      if (!data && propertyId) await fetchRoofEstimate(propertyId, true).unwrap().catch(() => undefined);
      // Save the quote first — if it fails we don't want to have already mutated tenant config.
      const saved = await runSave({
        engagementId,
        adjustments: areaOverride != null ? { ...adjustments(), area_sqft: areaOverride } : adjustments(),
      }).unwrap();
      // Only persist pricing-config changes for tenant admins when the admin actually changed a
      // pricing field (not just roof area). Guard against NaN from a cleared input.
      if (isTenantAdmin && adjusting) {
        const price = priceTouched ? parseFloat(priceInput) : (data?.price_per_unit ?? 0);
        const complexity = complexityTouched ? parseFloat(complexityInput) : (data?.complexity_factor ?? 1);
        const currentUnit = current?.pricing_basis.unit ?? data?.pricing_unit ?? 'square';
        const pricingChanged = (priceTouched && Number.isFinite(price) && price !== (data?.price_per_unit ?? 0))
          || (complexityTouched && Number.isFinite(complexity) && complexity !== (data?.complexity_factor ?? 1))
          || unitInput !== currentUnit;
        if (pricingChanged && Number.isFinite(price) && Number.isFinite(complexity)) {
          await updateCatalogueConfig({
            key: 'quick_quote_pricing',
            value: { price_per_unit: price, unit: unitInput, complexity_factor: complexity },
          }).unwrap();
        }
      }
      setAdjusting(false);
      setAreaInput(String(saved.inputs.area_sqft));
      setComplexityInput(String(saved.pricing_basis.complexity_factor));
      setPriceInput(String(saved.pricing_basis.price_per_unit));
      setUnitInput(saved.pricing_basis.unit ?? unitInput);
      setAreaTouched(saved.inputs.area_source === 'MANUAL');
      setComplexityTouched(saved.pricing_basis.complexity_source === 'AGENT_OVERRIDE');
      setPriceTouched(priceTouched && Number.isFinite(parseFloat(priceInput)));
    } catch (err) {
      setQuoteError(errorDetail(err, t('quickQuote.saveFailed', "Couldn't save the Quick Quote.")));
    }
  };

  // "Is there anything new to save?" — compared against the CURRENT saved quote, not merely
  // whether a field was touched. Touched-but-identical is not a change: after a save the fields
  // hold exactly what was saved (see onSave), so a repeat click would otherwise add a duplicate
  // version to the history for no reason.
  const pendingArea = areaTouched ? parseFloat(areaInput) : undefined;
  const pendingComplexity = complexityTouched ? parseFloat(complexityInput) : undefined;
  const pendingPrice = priceTouched ? parseFloat(priceInput) : undefined;
  const hasUnsavedChange = (pendingArea !== undefined && Number.isFinite(pendingArea)
        && pendingArea !== current?.inputs.area_sqft)
    || (pendingComplexity !== undefined && Number.isFinite(pendingComplexity)
        && pendingComplexity !== current?.pricing_basis.complexity_factor)
    || (pendingPrice !== undefined && Number.isFinite(pendingPrice)
        && pendingPrice !== current?.pricing_basis.price_per_unit)
    || (adjusting && unitInput !== (current?.pricing_basis.unit ?? data?.pricing_unit ?? 'square'));

  const startAdjusting = () => {
    setAreaTouched(false);
    setComplexityTouched(false);
    setPriceTouched(false);
    setAreaInput(current ? String(current.inputs.area_sqft) : data?.ai_roof_area_sqft != null ? String(data.ai_roof_area_sqft) : '');
    setComplexityInput(current ? String(current.pricing_basis.complexity_factor) : data?.complexity_factor != null ? String(data.complexity_factor) : '');
    setPriceInput(current ? String(current.pricing_basis.price_per_unit) : data?.price_per_unit != null ? String(data.price_per_unit) : '');
    setUnitInput(current?.pricing_basis.unit ?? data?.pricing_unit ?? 'square');
    setAdjusting(true);
  };

  // Finish on the satellite map (a new traced AREA): the traced roof area becomes the quote's roof
  // area - shown in the Edit form - and the quote is saved with it right away.
  const handleShapesChange = (next: DrawnShape[]) => {
    const gainedArea = next.filter((s) => s.type === 'polygon').length > shapes.filter((s) => s.type === 'polygon').length;
    setShapes(next);
    if (!gainedArea) return;
    const area = tracedRoofAreaSqft(next, activePitch);
    if (area == null || area <= 0) return;
    if (!adjusting) startAdjusting();
    setAreaInput(String(area));
    setAreaTouched(true);
    if (canCreate) void onSave(area);
  };

  const shownQuote = current;

  // Nothing measured and no quote on file: say so briefly instead of leaving the panel bare.
  const showCantQuote = !data && !isLoading && !saving && !shownQuote;

  return (
    <div className="overflow-hidden rounded-xl border border-ui-border">
      {/* The header doubles as the collapse control — clicking anywhere on it hides the card back
          to WorkflowCard's summary pill, so there's one bar here instead of a separate "Hide
          Quick Quote" toggle stacked on top of this title. */}
      <button
        type="button"
        onClick={onHide}
        className="flex w-full items-start justify-between gap-3 border-b border-ui-border bg-ui-subtle px-3 py-2.5 text-left hover:bg-ui-hover"
      >
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-wide text-ui-text2">
            {t('quickQuote.title', 'Quick Quote')}
          </p>
          {current && (
            /* Version and date only — NOT quick_quote_code. That code is
               `QQ-{engagement_id}-V{n}`, so printing it put a raw engagement UUID on screen:
               forty-odd characters of noise that told a Sales Agent nothing. The platform's own
               rule (see the sam_core prompt: "Never mention raw record IDs or UUIDs … use them
               only internally") exists for exactly this reason, and it applies to UI text as much
               as to SAM's replies. The code stays as the record's key and in logs, where it is
               genuinely useful for tracing one quote back to its engagement. */
            <p className="mt-0.5 truncate text-xs text-ui-text3">
              {t('quickQuote.versionLabel', 'Version {{n}}', { n: current.version_no })}
              {current.created_at && <> · {fmtDateTime(current.created_at)}</>}
            </p>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {/* EST-FR-04-004 — the record says it is indicative; the badge is always present, live
              estimate, preview, or saved quote alike, so it can never read as a formal estimate. */}
          <span className="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-medium text-warning-text">
            {t('quickQuote.indicative', 'Indicative')}
          </span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ui-text3">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </div>
      </button>

      {isLoading && !!shownQuote && (
        <p className="px-3 py-3 text-xs text-ui-text3">{t('quickQuote.loadingSatellite', 'Loading satellite view…')}</p>
      )}
      {isError && (
        <p className="px-3 py-3 text-xs text-ui-text3">{t('quickQuote.satelliteLoadFailed', "Couldn't load the satellite view for this address.")}</p>
      )}

      <div className="space-y-2 bg-ui-subtle p-3">
        {/* Nothing measured yet. The roof lookup is deliberately not run on open (that is what
            made Quick Quote look already-triggered on every engagement), so this states what the
            button will do rather than leaving an empty panel with two buttons in it. */}
        {/* The triggered flow is two calls back to back (measure, then quote), so it says what is
            happening rather than leaving the panel blank between them. */}
        {(saving || isLoading) && !shownQuote && (
          <p className="text-xs text-ui-text3">
            {t('quickQuote.generating', 'Measuring this roof and preparing a quote…')}
          </p>
        )}
        {data && (
          <>
            {data.ai_estimate_available ? (
              <p className="text-sm text-ui-text">
                <span className="font-semibold">{fmt(data.ai_roof_area_sqft)} sqft</span>
                {data.ai_squares != null && <span className="text-ui-text2"> · {fmt(data.ai_squares, 1)} squares</span>}
                {data.ai_roof_area_m2 != null && <span className="text-ui-text3"> ({fmt(data.ai_roof_area_m2)} m²)</span>}
                <span className="text-ui-text3"> — {t('quickQuote.aiEstimateSuffix', 'AI estimate')}</span>
              </p>
            ) : (
              <p className="text-sm text-ui-text2">
                {t('quickQuote.aiUnavailable', "Can't measure this roof automatically. Trace it on the map to get an estimate.")}
              </p>
            )}

            {(data.ai_estimate_available || data.predominant_pitch) && (
              <RoofPitchPanel
                predominantPitch={data.predominant_pitch}
                predominantPitchDegrees={data.predominant_pitch_degrees}
                pitchBreakdown={data.pitch_breakdown}
                segmentCount={data.segment_count}
                groundAreaSqft={data.ai_ground_area_sqft}
              />
            )}

            {(data.lat != null && data.lng != null) || data.address ? (
              /* The map is loaded ON DEMAND, not with the card. Google bills a "dynamic map load"
                 every time a Map is instantiated, and this section sits on the property card that
                 people open constantly for tasks/engagement — auto-loading meant paying for a map
                 almost nobody looked at. The numbers above (served from the durable cache, free)
                 are the part most visits actually need. */
              showMap ? (
                <>
                  <div className="h-[260px] w-full overflow-hidden rounded-lg border border-ui-border">
                    <QuickQuoteMap
                      ref={mapRef}
                      lat={data.lat}
                      lng={data.lng}
                      address={data.address}
                      onShapesChange={handleShapesChange}
                    />
                  </div>
                  {shapes.length > 0 && (
                    <TracedMeasurements
                      shapes={shapes}
                      activePitch={activePitch}
                      onRemove={(id) => mapRef.current?.removeShape(id)}
                    />
                  )}
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowMap(true)}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-ui-border px-3 py-3 text-xs font-medium text-accent-text hover:bg-ui-hover"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                    <line x1="8" y1="2" x2="8" y2="18" />
                    <line x1="16" y1="6" x2="16" y2="22" />
                  </svg>
                  {t('quickQuote.showMap', 'Show satellite map & measure')}
                </button>
              )
            ) : null}
          </>
        )}

        {/* Revisiting a quoted property does not re-measure it, so `data` (the roof lookup) is
            absent and the map above never renders — the satellite view disappeared entirely on a
            second visit. The capability is still wanted; it just must not fetch on its own. This
            asks for the measurement only when someone actually presses it, then the block above
            takes over and draws the map. */}
        {!data && !isLoading && (
          <button
            type="button"
            onClick={async () => {
              await fetchRoofEstimate(propertyId, true).unwrap().catch(() => undefined);
              setShowMap(true);
            }}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-ui-border px-3 py-3 text-xs font-medium text-accent-text hover:bg-ui-hover"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
              <line x1="8" y1="2" x2="8" y2="18" />
              <line x1="16" y1="6" x2="16" y2="22" />
            </svg>
            {t('quickQuote.showMap', 'Show satellite map & measure')}
          </button>
        )}

        {showCantQuote && (
          <p className="text-xs text-ui-text3">{t('quickQuote.cannotQuote', "Can't generate a quote for this roof.")}</p>
        )}

        <div className="space-y-1">
          <QuickQuoteCostEstimate
            low={shownQuote ? shownQuote.result.estimated_cost_low : data?.estimated_cost_low}
            high={shownQuote ? shownQuote.result.estimated_cost_high : data?.estimated_cost_high}
            unit={shownQuote ? shownQuote.pricing_basis.unit : data?.pricing_unit}
            billableQuantity={shownQuote ? shownQuote.result.billable_quantity : data?.billable_quantity}
            pricePerUnit={shownQuote ? shownQuote.pricing_basis.price_per_unit : data?.price_per_unit}
            complexityFactor={shownQuote ? shownQuote.pricing_basis.complexity_factor : data?.complexity_factor}
          />
          {shownQuote && <QuoteBasis quote={shownQuote} />}
          {!shownQuote && data && data.ai_estimate_available && data.estimated_cost_low == null && (
            <p className="text-xs text-ui-text3">
              {t('quickQuote.emptyHint', 'No Quick Quote saved yet. Generate a ballpark from the roof measurement — no assessment needed.')}
            </p>
          )}
        </div>

        {quoteError && !(showCantQuote && /no roof area/i.test(quoteError)) && (/no roof area/i.test(quoteError)
          ? <p className="text-xs text-ui-text3">{t('quickQuote.cannotQuote', "Can't generate a quote for this roof.")}</p>
          : <p className="text-xs font-medium text-danger-text">{quoteError}</p>)}

        {adjusting && (
          <div className="space-y-2 rounded-lg border border-ui-border bg-ui-card px-2.5 py-2.5">
            <div className="flex flex-wrap gap-2">
              <label className="flex-1 min-w-[110px] text-xs text-ui-text3">
                Roof area (sqft)
                <input
                  type="number" min="1" step="1" inputMode="decimal"
                  value={areaInput}
                  onChange={(e) => { setAreaInput(e.target.value); setAreaTouched(true); }}
                  className="mt-0.5 w-full rounded-lg border border-ui-border bg-ui-input px-2 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
                />
              </label>
              <label className="flex-1 min-w-[110px] text-xs text-ui-text3">
                Measurement unit
                <select
                  value={unitInput}
                  onChange={(e) => setUnitInput(e.target.value)}
                  className="mt-0.5 w-full rounded-lg border border-ui-border bg-ui-input px-2 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
                >
                  {UNIT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="flex-1 min-w-[110px] text-xs text-ui-text3">
                Price per unit ($)
                <input
                  type="number" min="0.01" step="0.01" inputMode="decimal"
                  value={priceInput}
                  onChange={(e) => { setPriceInput(e.target.value); setPriceTouched(true); }}
                  className="mt-0.5 w-full rounded-lg border border-ui-border bg-ui-input px-2 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
                />
              </label>
              <label className="flex-1 min-w-[110px] text-xs text-ui-text3">
                Complexity factor (1–3)
                <input
                  type="number" min="1" max="3" step="0.05" inputMode="decimal"
                  value={complexityInput}
                  onChange={(e) => { setComplexityInput(e.target.value); setComplexityTouched(true); }}
                  className="mt-0.5 w-full rounded-lg border border-ui-border bg-ui-input px-2 py-1.5 text-xs text-ui-text focus:border-ui-ring focus:outline-none focus:ring-1 focus:ring-ui-ring"
                />
              </label>
            </div>
            {isTenantAdmin && (
              <p className="text-xs text-ui-text3">Unit, price & complexity changes update your default pricing for all properties.</p>
            )}
          </div>
        )}

        {canCreate && (
          <div className="flex flex-wrap items-center justify-end gap-2 pt-0.5">
            {adjusting ? (
              <Button size="sm" variant="ghost" onClick={() => { setAdjusting(false); setQuoteError(null); }}>
                Cancel
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={startAdjusting}>
                Edit quote
              </Button>
            )}
            <Button
              size="sm"
              loading={saving}
              disabled={!!current && !hasUnsavedChange}
              onClick={() => void onSave()}
            >
              Save quote
            </Button>
          </div>
        )}
      </div>

    </div>
  );
}

export default PropertyRoofEstimateSection;
